// Admin dashboard — usage stats + a sortable per-player table. Behind a shared
// secret (ADMIN_KEY): the key is entered once, kept in sessionStorage for the
// tab, and sent on every request via the x-admin-key header. Reached at /admin
// (see App.tsx). Read-only; surfaces only public game state.
import { useEffect, useMemo, useState } from "react";
import {
  usageMetrics,
  USAGE_MIN_DENOM,
  USAGE_MIN_SESSIONS,
  USAGE_WINDOW_DAYS,
  type AdminPlayerRow,
  type AdminStatsView,
  type RaceMode,
} from "@shared/admin";
import { formatMs, formatPct } from "@shared/scoring";
import { adminDeleteProfile, adminStats } from "../lib/api";
import SceneBackground from "../components/brand/SceneBackground";
import Wordmark from "../components/brand/Wordmark";

const KEY_STORE = "br:admin-key";

type SortKey =
  | "displayName"
  | "plays"
  | "topScore"
  | "bestTimeMs"
  | "avgAccuracy"
  | "badges"
  | "lastPlayedAt";

interface Column {
  key: SortKey;
  label: string;
  numeric: boolean;
}

const COLUMNS: Column[] = [
  { key: "displayName", label: "Player", numeric: false },
  { key: "plays", label: "Plays", numeric: true },
  { key: "topScore", label: "Top score", numeric: true },
  { key: "bestTimeMs", label: "Best time", numeric: true },
  { key: "avgAccuracy", label: "Avg acc.", numeric: true },
  { key: "badges", label: "Badges", numeric: true },
  { key: "lastPlayedAt", label: "Last played", numeric: true },
];

function shortDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

/** Sort comparator. Nulls always sort last regardless of direction. */
function compare(a: AdminPlayerRow, b: AdminPlayerRow, key: SortKey, dir: 1 | -1): number {
  const av = a[key];
  const bv = b[key];
  if (av == null && bv == null) return 0;
  if (av == null) return 1;
  if (bv == null) return -1;
  if (typeof av === "string" && typeof bv === "string") {
    return av.localeCompare(bv) * dir;
  }
  if (key === "lastPlayedAt") {
    return (Date.parse(av as string) - Date.parse(bv as string)) * dir;
  }
  return ((av as number) - (bv as number)) * dir;
}

function StatCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="panel-tight px-4 py-3">
      <div className="text-xs font-bold uppercase tracking-wide text-ink/55">{label}</div>
      <div className="mt-0.5 font-display text-2xl text-ink tabular-nums sm:text-3xl">{value}</div>
      {hint && <div className="text-[11px] font-semibold text-ink/45">{hint}</div>}
    </div>
  );
}

const MODE_LABELS: Record<RaceMode, string> = {
  solo: "🏁 Solo",
  daily: "📅 Daily",
  quick: "🏎️ Quick Race",
  room: "👥 Friends room",
};

/** Percent, or "—" when the sample was too thin for the rate to mean anything
 *  (usageMetrics() returns null in that case — see USAGE_MIN_DENOM). */
const pct = (v: number | null): string => (v == null ? "—" : formatPct(v));

export default function Admin({ onHome }: { onHome: () => void }) {
  const [key, setKey] = useState(() => sessionStorage.getItem(KEY_STORE) ?? "");
  const [input, setInput] = useState("");
  const [stats, setStats] = useState<AdminStatsView | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Sort defaults to the server order (most plays first).
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({
    key: "plays",
    dir: -1,
  });

  // Operator-assisted deletion (COPPA §312.6, Tier 3): a two-step confirm per row.
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  async function deleteRow(playerId: string) {
    setDeletingId(playerId);
    setError("");
    try {
      await adminDeleteProfile(key, playerId);
      setStats((s) =>
        s ? { ...s, players: s.players.filter((p) => p.playerId !== playerId) } : s,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't delete that profile.");
    } finally {
      setDeletingId(null);
      setConfirmingId(null);
    }
  }

  useEffect(() => {
    if (!key) return;
    let active = true;
    setLoading(true);
    setError("");
    adminStats(key)
      .then((r) => active && setStats(r.stats))
      .catch((e) => {
        if (!active) return;
        setStats(null);
        // A bad key shouldn't stick around — drop it so the gate reappears.
        if (e instanceof Error && /not authorized/i.test(e.message)) {
          sessionStorage.removeItem(KEY_STORE);
          setKey("");
        }
        setError(e instanceof Error ? e.message : "Couldn't load the dashboard.");
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [key]);

  const submitKey = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed) return;
    sessionStorage.setItem(KEY_STORE, trimmed);
    setKey(trimmed);
  };

  const signOut = () => {
    sessionStorage.removeItem(KEY_STORE);
    setKey("");
    setStats(null);
    setInput("");
  };

  const toggleSort = (k: SortKey) =>
    setSort((s) =>
      s.key === k ? { key: k, dir: (s.dir * -1) as 1 | -1 } : { key: k, dir: k === "displayName" ? 1 : -1 },
    );

  const rows = useMemo(() => {
    if (!stats) return [];
    return [...stats.players].sort((a, b) => compare(a, b, sort.key, sort.dir));
  }, [stats, sort]);

  const summary = stats?.summary;

  // Funnel + conversion rates, derived from the raw kind→count map. Kept in
  // shared/admin.ts so the maths is unit-tested rather than trusted.
  const usage = useMemo(
    () => (stats ? usageMetrics(stats.usage) : null),
    [stats],
  );

  return (
    <div className="relative min-h-full">
      <SceneBackground showSpeed={false} />
      <main className="mx-auto w-full max-w-5xl px-4 pb-16 pt-4">
        <div className="mb-2 flex items-center justify-between">
          <button type="button" className="btn-chunk bg-white px-4 py-2 text-sm" onClick={onHome}>
            ← Home
          </button>
          {key && (
            <button type="button" className="btn-chunk bg-white px-4 py-2 text-sm" onClick={signOut}>
              Sign out
            </button>
          )}
        </div>

        <div className="mb-4 flex justify-center">
          <Wordmark width={220} />
        </div>

        {!key ? (
          /* ---- key gate ---- */
          <div className="panel mx-auto max-w-sm p-6 sm:p-7">
            <h2 className="text-center font-display text-2xl text-ink">🔒 Admin</h2>
            <p className="mt-1 text-center text-sm font-medium text-ink/55">
              Enter the admin key to view usage stats.
            </p>
            <form onSubmit={submitKey} className="mt-4 space-y-3">
              <input
                type="password"
                autoFocus
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Admin key"
                aria-label="Admin key"
                className="w-full rounded-2xl border-[3px] border-ink bg-white px-3 py-2.5 font-semibold text-ink outline-none focus:border-flame"
              />
              <button type="submit" className="btn-chunk w-full bg-flame py-3 text-white">
                Enter
              </button>
            </form>
            {error && <p className="mt-3 text-center text-sm font-semibold text-flame">{error}</p>}
          </div>
        ) : (
          /* ---- dashboard ---- */
          <div className="space-y-5">
            <h2 className="text-center font-display text-2xl text-ink sm:text-3xl">
              📊 Admin Dashboard
            </h2>

            {loading && !stats ? (
              <p className="py-10 text-center font-semibold text-ink/40">Loading the numbers…</p>
            ) : error && !stats ? (
              <p className="py-10 text-center font-semibold text-flame">{error}</p>
            ) : summary ? (
              <>
                {/* summary tiles */}
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <StatCard label="Players" value={String(summary.totalPlayers)} hint={`${summary.claimedPlayers} claimed`} />
                  <StatCard label="Scores posted" value={String(summary.totalGames)} hint="results recorded" />
                  <StatCard label="Multiplayer races" value={String(summary.totalRaces)} hint="rooms created" />
                  <StatCard label="Badges earned" value={String(summary.totalBadges)} />
                  <StatCard label="Active (7d)" value={String(summary.activePlayers7d)} hint="seen this week" />
                  <StatCard
                    label="Games/session"
                    value={
                      summary.windowSessions < USAGE_MIN_SESSIONS
                        ? "—"
                        : summary.avgGamesPerSession.toFixed(1)
                    }
                    hint={
                      summary.windowSessions < USAGE_MIN_SESSIONS
                        ? `${USAGE_WINDOW_DAYS}d · ${summary.windowSessions} sessions (low data)`
                        : `${USAGE_WINDOW_DAYS}d · ${summary.windowSessions} sessions`
                    }
                  />
                  <StatCard label="Avg accuracy" value={formatPct(summary.avgAccuracy)} hint="all results" />
                  <StatCard label="Profiles claimed" value={String(summary.claimedPlayers)} hint="recovery set" />
                </div>

                {/* ---- funnel + conversion (aggregate day tallies, no identity) ---- */}
                {usage && (
                  <div className="panel overflow-hidden p-0">
                    <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 px-5 py-3">
                      <h3 className="font-display text-xl text-ink">Funnel</h3>
                      <span className="text-xs font-semibold text-ink/45">
                        last {USAGE_WINDOW_DAYS}d · rates hidden below {USAGE_MIN_DENOM} events
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-3 px-5 sm:grid-cols-4">
                      <StatCard
                        label="Completion"
                        value={pct(usage.completionRate)}
                        hint={`${usage.window.finishes} of ${usage.window.starts} started`}
                      />
                      <StatCard
                        label="Claim rate"
                        value={pct(usage.claimRate)}
                        hint="racers saved per finish"
                      />
                      <StatCard
                        label="Share rate"
                        value={pct(usage.shareRate)}
                        hint="shares per finish"
                      />
                      <StatCard
                        label="Score posted"
                        value={pct(usage.postRate)}
                        hint="per solo/daily finish"
                      />
                    </div>

                    <div className="mt-3 overflow-x-auto">
                      <table className="w-full min-w-[34rem] border-collapse text-sm">
                        <thead>
                          <tr className="bg-ink/[0.04] text-left">
                            <th className="px-5 py-2 text-xs font-bold uppercase tracking-wide text-ink/55">
                              Mode
                            </th>
                            <th className="px-3 py-2 text-right text-xs font-bold uppercase tracking-wide text-ink/55">
                              Picked
                            </th>
                            <th className="px-3 py-2 text-right text-xs font-bold uppercase tracking-wide text-ink/55">
                              Started
                            </th>
                            <th className="px-3 py-2 text-right text-xs font-bold uppercase tracking-wide text-ink/55">
                              Finished
                            </th>
                            <th className="px-3 py-2 text-right text-xs font-bold uppercase tracking-wide text-ink/55">
                              Start&nbsp;%
                            </th>
                            <th className="px-5 py-2 text-right text-xs font-bold uppercase tracking-wide text-ink/55">
                              Finish&nbsp;%
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {usage.funnel.map((r) => (
                            <tr key={r.mode} className="border-t-[2px] border-ink/10">
                              <td className="px-5 py-2 font-bold text-ink">{MODE_LABELS[r.mode]}</td>
                              <td className="px-3 py-2 text-right tabular-nums text-ink/70">{r.picks}</td>
                              <td className="px-3 py-2 text-right tabular-nums text-ink/70">{r.starts}</td>
                              <td className="px-3 py-2 text-right tabular-nums text-ink/70">{r.finishes}</td>
                              <td className="px-3 py-2 text-right tabular-nums text-ink/70">{pct(r.startRate)}</td>
                              <td className="px-5 py-2 text-right font-bold tabular-nums text-ink">{pct(r.completionRate)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <p className="px-5 pb-4 pt-3 text-[11px] font-semibold leading-relaxed text-ink/45">
                      Aggregate day tallies with no identifier, so these are ratios of
                      totals across all players — not per-player conversion. Leaderboard
                      opened {usage.leaderboardViews}×.
                    </p>
                  </div>
                )}

                {/* player table */}
                <div className="panel overflow-hidden p-0">
                  <div className="flex items-center justify-between px-5 py-3">
                    <h3 className="font-display text-xl text-ink">Players</h3>
                    <span className="text-xs font-semibold text-ink/45">{rows.length} total</span>
                  </div>
                  {rows.length === 0 ? (
                    <p className="px-5 pb-6 pt-2 text-center font-semibold text-ink/50">
                      No players yet.
                    </p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse text-sm">
                        <thead>
                          <tr className="border-y-[3px] border-ink/10 bg-ink/[0.03] text-left">
                            {COLUMNS.map((c) => {
                              const active = sort.key === c.key;
                              return (
                                <th
                                  key={c.key}
                                  className={`px-4 py-2.5 font-bold uppercase tracking-wide text-ink/60 ${
                                    c.numeric ? "text-right" : "text-left"
                                  }`}
                                >
                                  <button
                                    type="button"
                                    onClick={() => toggleSort(c.key)}
                                    className={`inline-flex items-center gap-1 text-xs hover:text-flame ${
                                      c.numeric ? "flex-row-reverse" : ""
                                    } ${active ? "text-flame" : ""}`}
                                    aria-label={`Sort by ${c.label}`}
                                  >
                                    {c.label}
                                    <span aria-hidden className="text-[10px]">
                                      {active ? (sort.dir === -1 ? "▼" : "▲") : "↕"}
                                    </span>
                                  </button>
                                </th>
                              );
                            })}
                            <th className="px-4 py-2.5 text-right text-xs font-bold uppercase tracking-wide text-ink/60">
                              Delete
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((p) => (
                            <tr
                              key={p.playerId}
                              className="border-b border-ink/10 last:border-0 hover:bg-sunny/10"
                            >
                              <td className="px-4 py-2.5">
                                <div className="flex items-center gap-2">
                                  <span className="text-lg leading-none" aria-hidden>
                                    {p.avatar || "🙂"}
                                  </span>
                                  <span className="min-w-0">
                                    <span className="block truncate font-bold text-ink">
                                      {p.displayName}
                                    </span>
                                    {p.handle && (
                                      <span className="block truncate text-xs font-semibold text-ink/45">
                                        @{p.handle}
                                      </span>
                                    )}
                                  </span>
                                  {p.claimed && (
                                    <span title="Claimed profile" aria-label="Claimed profile">
                                      🔑
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-2.5 text-right font-semibold text-ink tabular-nums">
                                {p.plays}
                              </td>
                              <td className="px-4 py-2.5 text-right font-semibold text-ink tabular-nums">
                                {p.plays > 0 ? p.topScore : "—"}
                              </td>
                              <td className="px-4 py-2.5 text-right text-ink/80 tabular-nums">
                                {p.bestTimeMs != null ? formatMs(p.bestTimeMs) : "—"}
                              </td>
                              <td className="px-4 py-2.5 text-right text-ink/80 tabular-nums">
                                {p.plays > 0 ? formatPct(p.avgAccuracy) : "—"}
                              </td>
                              <td className="px-4 py-2.5 text-right text-ink/80 tabular-nums">
                                {p.badges}
                              </td>
                              <td className="px-4 py-2.5 text-right text-ink/70 tabular-nums">
                                {shortDate(p.lastPlayedAt)}
                              </td>
                              <td className="whitespace-nowrap px-4 py-2.5 text-right">
                                {confirmingId === p.playerId ? (
                                  <span className="inline-flex items-center gap-1">
                                    <button
                                      type="button"
                                      onClick={() => deleteRow(p.playerId)}
                                      disabled={deletingId === p.playerId}
                                      className="rounded-full border-[2px] border-flame bg-flame px-2 py-0.5 text-xs font-bold text-white disabled:opacity-60"
                                    >
                                      {deletingId === p.playerId ? "…" : "Confirm"}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setConfirmingId(null)}
                                      className="rounded-full border-[2px] border-ink/15 px-2 py-0.5 text-xs font-bold text-ink/60"
                                    >
                                      Cancel
                                    </button>
                                  </span>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => setConfirmingId(p.playerId)}
                                    title="Delete this profile (parental request)"
                                    aria-label={`Delete ${p.displayName}`}
                                    className="text-ink/40 hover:text-flame"
                                  >
                                    🗑
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            ) : null}
          </div>
        )}
      </main>
    </div>
  );
}
